import { Button } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import styled from "styled-components/native";
import useBulkInitScreenModals from "./useBulkInitScreenModal";
import {
  formatNumberWithPrecision,
  removePercentage,
} from "@utils/product/productFormatters";
import { DefaultTheme } from "styled-components/native";

const EditBulkItemButtons = ({
  formdata,
  productNameShort,
  removeFromList,
  handleEvent,
  item,
  fromRequestModify = false,
  testIDPrefix = "bulk-init-screen.edit-item",
}: any) => {
  const { t } = useTranslation();
  const { dynamicFormValues, setRequestFormValue, isDynamicFormValid } =
    useDynamicFormState();
  const { renderModals, showModal } = useBulkInitScreenModals();
  const handleSaveAction = () => {
    const itemId = fromRequestModify ? item?.id : item?.item?.id;
    const formValues = dynamicFormValues?.singleForm[itemId] || {};
    const updatedItem = fromRequestModify ? item : item?.item || {};
    const offerTotalDiscount =
      dynamicFormValues?.requestForm.offerTotalDiscount;

    const pricePerOffer = formValues?.pricePerOffer?.replace(/,/g, "");
    const totalOfferPrice = formValues?.totalOfferPrice?.replace(/,/g, "");
    const totalOfferDiscount = removePercentage(formValues?.totalOfferDiscount);

    const updatedPricePerCarat =
      updatedItem?.updated_price_per_carat?.toString();
    const updatedTotalSalesPrice =
      updatedItem?.updated_total_sales_price?.toString();
    const updatedDiscountPercent = removePercentage(
      updatedItem?.updated_discount_percent?.toString()
    );

    const originalPricePerCarat = formatNumberWithPrecision(
      Number(updatedItem?.price_per_carat),
      { precision: 0 }
    );
    const originalTotalSalesPrice = updatedItem?.total_sales_price?.toString();
    const originalDiscountPercent = removePercentage(
      updatedItem?.discount_percent?.toString() || "-"
    );

    const isUpdatedValuesMatching =
      pricePerOffer === updatedPricePerCarat &&
      totalOfferPrice === updatedTotalSalesPrice &&
      totalOfferDiscount === updatedDiscountPercent;

    const isOriginalValuesMatching =
      pricePerOffer === originalPricePerCarat &&
      totalOfferPrice === originalTotalSalesPrice &&
      totalOfferDiscount === originalDiscountPercent;
    const isOriginalCommentsMatching =
      formValues.comments === updatedItem.comments;

    if (isUpdatedValuesMatching || isOriginalValuesMatching) {
      if (isOriginalValuesMatching) {
        if (!isOriginalCommentsMatching) {
          handleEvent({ comments: formValues.comments });
        } else {
          handleEvent();
        }
      } else {
        handleEvent(formValues);
      }
    } else {
      if (formdata.length > 1) {
        if (offerTotalDiscount) {
          showModal("delete-total-discount", () => {
            setRequestFormValue("offerTotalDiscount", "");
            handleEvent(formValues);
          });
        } else {
          showModal("delete-total-price", () => {
            handleEvent(formValues);
          });
        }
      } else {
        handleEvent(formValues);
      }
    }
  };

  return (
    <ButtonContainer>
      {!fromRequestModify && (
        <ChildButtonContainer
          data-testId={testIDPrefix + ".remove-from-list-button"}
          testID={testIDPrefix + ".remove-from-list-button"}
        >
          <Button
            type={ButtonType.secondary}
            category={productNameShort}
            title={t("bulkInitScreen.modal.removeFromList.button.text")}
            onPress={removeFromList}
            loading={false}
            disabled={false}
          />
        </ChildButtonContainer>
      )}
      <ChildButtonContainer
        fullWidth={fromRequestModify}
        data-testId={testIDPrefix + ".save-button"}
        testID={testIDPrefix + ".save-button"}
      >
        <Button
          type={ButtonType.primary}
          category={productNameShort}
          title={t("bulkInitScreen.modal.save.button.text")}
          onPress={() => handleSaveAction()}
          loading={false}
          disabled={!(isDynamicFormValid as any)?.singleForm?.isValid}
        />
      </ChildButtonContainer>
      {renderModals()}
    </ButtonContainer>
  );
};

const ButtonContainer = styled.View<any>`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "column" : "row"};
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Double}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile ? SpaceType.Extra : 0}px;
  align-items: center;
  gap: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "20px" : Platform.OS !== "web" ? "10px" : "2%"};
`;

const ChildButtonContainer = styled.View<{
  fullWidth?: boolean;
}>`
  width: ${({
    theme,
    fullWidth,
  }: {
    theme: DefaultTheme;
    fullWidth?: boolean;
  }) => (theme.isMobile || fullWidth ? "100%" : "49%")};
`;

export default EditBulkItemButtons;
